import { useEffect, useRef, useState } from 'react'
import { useAppStore } from '@/store/appStore'
import { getMediaBlob } from '@/db/viewerDb'
import { mediaNameFromUrl } from '@/utils/mediaReferences'

/**
 * Resolves a single image URL for offline rendering.
 *
 * When online, returns the URL unchanged.
 * When offline, looks up the blob in IndexedDB and returns a blob: URL.
 * Returns null if the image is not in the offline cache.
 * Returns undefined while the lookup is in progress.
 */
export function useOfflineMediaUrl(url: string | null | undefined): string | null | undefined {
  const { isOffline } = useAppStore()
  const [result, setResult] = useState<string | null | undefined>(!isOffline ? url : undefined)
  const blobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isOffline) {
      // Revoke any existing blob: URL when going back online
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
      setResult(url)
      return
    }

    if (!url) {
      setResult(null)
      return
    }

    let cancelled = false

    const key = mediaNameFromUrl(url) ?? url

    getMediaBlob(key).then((entry) => {
      if (cancelled) return
      if (!entry) {
        setResult(null)
        return
      }
      const objectUrl = URL.createObjectURL(entry.blob)
      blobUrlRef.current = objectUrl
      setResult(objectUrl)
    })

    return () => {
      cancelled = true
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [url, isOffline])

  return result
}
